import { useTranslation } from 'react-i18next';

import { InvitationStatus } from 'contacts/enums';
import { useContactActions } from 'contacts/hooks';
import { IContact, IContactGroup } from 'contacts/types';
import { Button } from 'core/components';
import { Color, Variant } from 'theme/enums';

interface IProps {
  contact: IContact;
  onSuccess?: (contact: IContact, contactGroup?: IContactGroup) => void;
  disabled?: boolean;
}

export const InvitationButton = ({ contact, onSuccess, disabled }: IProps) => {
  const { t } = useTranslation();

  const { sendInvitation, revokeInvitation, isSendInvitationLoading, isRevokeInvitationLoading } =
    useContactActions();

  const isLoading = isSendInvitationLoading || isRevokeInvitationLoading;

  const isInvited = contact.invitationStatus === InvitationStatus.INVITED;
  const isActive = contact.invitationStatus === InvitationStatus.ACTIVE;
  const isNotInvited = contact.invitationStatus === InvitationStatus.NOT_INVITED;

  const shouldShowRevokeButton = isInvited || isActive;
  const shouldShowSendButton = isNotInvited;

  const handleSendInvitation = async () => {
    const { success, contact: updatedContact } = await sendInvitation(contact.id);

    if (!success) {
      return false;
    }

    onSuccess?.(updatedContact, updatedContact?.group);
  };

  const handleRevokeInvitation = async () => {
    const { success, contact: updatedContact } = await revokeInvitation(contact.id);

    if (!success) {
      return false;
    }

    onSuccess?.(updatedContact, updatedContact?.group);
  };

  if (shouldShowSendButton) {
    return (
      <Button
        text={t('buttonLabelSendInvitation')}
        color={Color.PRIMARY}
        variant={Variant.SOLID}
        onClick={handleSendInvitation}
        disabled={disabled || isLoading}
      />
    );
  }

  if (shouldShowRevokeButton) {
    return (
      <Button
        text={t('buttonLabelRevokeAccess')}
        color={Color.DANGER}
        variant={Variant.SOLID}
        onClick={handleRevokeInvitation}
        disabled={disabled || isLoading}
      />
    );
  }

  return null;
};
